import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router';
import { paths } from './paths';
import { usePwa } from './usePwa';
import { useLogout, useSession } from '../api/queries';
import { LoginRoute } from '../routes/LoginRoute';
import { PicksRoute } from '../routes/PicksRoute';
import {
  HistoryRoute,
  HomeRoute,
  ResultsRoute,
  SettingsRoute,
  StandingsRoute,
} from '../routes/player';
import { BroadcastLandingRoute, CommishBroadcastRoute } from '../routes/broadcast';
import {
  CommishHomeRoute,
  CommishMembersRoute,
  CommishReplaceRoute,
  CommishResultsRoute,
  CommishSlateRoute,
} from '../routes/commish';
import { AppShell } from '../ui/AppShell';
import { NotFound } from '../ui/screens/player';
import { BootSplash } from './BootSplash';
import type { NavItem } from '../screens/contracts';

/**
 * Routing.
 *
 * Auth routes render bare — a signed-out visitor has no navigation to show. Everything
 * else renders inside the shell, and the commissioner subtree sits behind a second guard
 * that checks the role resolved from the server, never from anything the client holds.
 */
export function App() {
  return (
    <Routes>
      <Route path={paths.login} element={<LoginRoute initialMode="login" />} />
      <Route path={paths.register} element={<LoginRoute initialMode="register" />} />
      <Route path={paths.forgot} element={<LoginRoute initialMode="forgot" />} />
      <Route path={paths.reset} element={<LoginRoute initialMode="reset" />} />
      <Route path="/join/:code" element={<LoginRoute initialMode="register" />} />

      {/*
        Outside the auth guard, and outside the shell. Someone arrives here by tapping a
        notification on a lock screen; bouncing them to a login form would guarantee the
        message never gets read. The slug is unguessable and the server refuses to resolve
        one that has not been sent, so nothing is exposed by leaving it open.
      */}
      <Route path="/x/:slug" element={<BroadcastLandingRoute />} />

      <Route element={<RequireAuth />}>
        <Route path={paths.root} element={<HomeRoute />} />
        <Route path={paths.picks} element={<PicksRoute />} />
        <Route path="/picks/:weekId" element={<PicksRoute />} />
        <Route path={paths.standings} element={<StandingsRoute />} />
        <Route path={paths.results} element={<ResultsRoute />} />
        <Route path={paths.history} element={<HistoryRoute />} />
        <Route path={paths.settings} element={<SettingsRoute />} />

        <Route element={<RequireCommissioner />}>
          <Route path={paths.commish} element={<CommishHomeRoute />} />
          <Route path={paths.commishSlate} element={<CommishSlateRoute />} />
          <Route path="/commish/slate/add" element={<CommishReplaceRoute />} />
          <Route path="/commish/slate/:slateGameId/replace" element={<CommishReplaceRoute />} />
          <Route path={paths.commishResults} element={<CommishResultsRoute />} />
          <Route path={paths.commishMembers} element={<CommishMembersRoute />} />
          <Route path={paths.commishBroadcast} element={<CommishBroadcastRoute />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFound homeHref={paths.root} />} />
    </Routes>
  );
}

/** Renders the shell only once we know who the user is. */
function RequireAuth() {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) return <BootSplash />;

  if (session.isError || !session.data) {
    // Remember where they were headed so login can return them there.
    return <Navigate to={paths.login} replace state={{ from: location.pathname }} />;
  }

  return <Shell />;
}

function RequireCommissioner() {
  const session = useSession();
  if (session.isLoading) return <BootSplash />;
  if (session.data?.activeRole !== 'commissioner') {
    return <Navigate to={paths.root} replace />;
  }
  return <Outlet />;
}

/** The chrome: header, navigation, update prompt. */
function Shell() {
  const session = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const pwa = usePwa();

  const isCommissioner = session.data?.activeRole === 'commissioner';

  const isActive = (href: string) =>
    href === paths.root ? location.pathname === href : location.pathname.startsWith(href);

  const nav: NavItem[] = [
    { key: 'home', label: 'Home', href: paths.root, icon: 'home', isActive: isActive(paths.root) },
    { key: 'picks', label: 'Picks', href: paths.picks, icon: 'picks', isActive: isActive(paths.picks) },
    {
      key: 'standings',
      label: 'Standings',
      href: paths.standings,
      icon: 'standings',
      isActive: isActive(paths.standings),
    },
    {
      key: 'results',
      label: 'Results',
      href: paths.results,
      icon: 'results',
      isActive: isActive(paths.results),
    },
  ];

  if (isCommissioner) {
    nav.push({
      key: 'commish',
      label: 'Commish',
      href: paths.commish,
      icon: 'commish',
      isActive: isActive(paths.commish),
    });
  }

  return (
    <AppShell
      nav={nav}
      user={
        session.data
          ? { displayName: session.data.user.displayName, email: session.data.user.email }
          : null
      }
      leagueName={session.data?.memberships[0]?.leagueName ?? null}
      weekLabel={session.data?.currentWeek?.label ?? null}
      onLogout={() => logout.mutate(undefined, { onSuccess: () => navigate(paths.login) })}
      updateReady={pwa.updateReady}
      onApplyUpdate={pwa.applyUpdate}
    >
      <Outlet />
    </AppShell>
  );
}
